import { authenticatedBitswanBackendInstance } from "@/lib/api-client";
import { getActiveOrgFromCookies } from "./organisations";

type AutomationServerListResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: AutomationServer[];
};

export type AutomationServer = {
  id: number;
  name: string;
  group_memberships?: AutomationServerGroupMembership[];
  automation_server_id: string;
  is_connected: boolean;
  server_type: 'byos' | 'hetzner';
  domain: string | null;
  // Created for CLI registration but not yet registered, OTP still live.
  registration_pending?: boolean;
  bailey_url: string | null;
  // CLI version the daemon last self-reported; null until it does.
  bailey_version: string | null;
  bailey_version_reported_at: string | null;
  supports_server_recovery: boolean;
  provisioning_status: string | null;
  ipv4_address: string | null;
  ipv6_address: string | null;
  hetzner_server_type: string | null;
  hetzner_datacenter: string | null;
  updated_at: string;
  created_at: string;
};

export type HetznerServerConfig = {
  id: string;
  hetzner_server_type: string;
  display_name: string;
  description: string | null;
  vcpus: number;
  memory_gb: number;
  disk_gb: number;
  monthly_price: string | null;
  currency: string;
  available_locations: string[];
  trial_eligible?: boolean;
  disabled_reason?: string | null;
};

export type HetznerRegion = {
  hetzner_location_id: string;
  display_name: string;
};

export type HetznerOptions = {
  configs: HetznerServerConfig[];
  regions: HetznerRegion[];
  currency: string;
};

export type ProvisioningStatus = {
  automation_server_id: string;
  provisioning_status: string;
  provisioning_error: string | null;
  ipv4_address: string | null;
  ipv6_address: string | null;
  domain: string | null;
  server_type: string;
};

export type AutomationServerGroupMembership = {
  id: number;
  automation_server: string;
  keycloak_group_id: string;
};

export async function getAutomationServers(
  page: number = 1,
  search?: string,
  orgId?: string,
) {
  const bitswanBEInstance = await authenticatedBitswanBackendInstance(
    orgId ? { 'X-Org-Id': orgId } : {},
  );

  const activeOrg = await getActiveOrgFromCookies();

  const params: Record<string, string | number> = {
    page: page,
  };
  if (search && search.trim()) {
    params.search = search.trim();
  }

  const res = await bitswanBEInstance.get("/automation-servers/", {
    params,
    headers: {
      "X-Org-Id": orgId ?? activeOrg?.id ?? "",
    },
  });

  return res.data as AutomationServerListResponse;
}

export async function deleteAutomationServer(serverId: string) {
  const bitswanBEInstance = await authenticatedBitswanBackendInstance();

  const activeOrg = await getActiveOrgFromCookies();

  const res = await bitswanBEInstance.delete(`/automation-servers/${serverId}/`, {
    headers: {
      "X-Org-Id": activeOrg?.id ?? "",
    },
  });

  return res.data;
}

export async function createAutomationServerWithOTP(
  name: string,
  domain?: string,
  orgId?: string,
) {
  const apiClient = await authenticatedBitswanBackendInstance();
  const activeOrg = orgId ? null : await getActiveOrgFromCookies();

  const body: Record<string, string> = { name: name.trim() };
  if (domain?.trim()) {
    body.domain = domain.trim();
  }

  const response = await apiClient.post('/automation-servers/create-with-otp', body, {
    headers: {
      "X-Org-Id": orgId ?? activeOrg?.id ?? "",
    },
  });

  return response.data;
}

export async function checkAutomationServerOTPStatus(automationServerId: string) {
  const activeOrg = await getActiveOrgFromCookies();
  const apiClient = await authenticatedBitswanBackendInstance();
  const response = await apiClient.get(
    `/automation-servers/check-otp-status?automation_server_id=${automationServerId}`,
    {
      headers: {
        "X-Org-Id": activeOrg?.id ?? "",
      },
    }
  );

  return response.data;
}

// --- Disaster recovery ------------------------------------------------------
// Rebuilding a lost server needs an access token on a machine where nothing
// exists yet, and that token also decides which backup bucket the CLI reaches.
// So the OTP is minted against the EXISTING server record: the operator cannot
// end up pointed at a fresh, empty repo. GET reports what recovery would do
// without issuing anything; POST mints and returns the command to run.

export type DisasterRecoveryRepo = {
  bucket: string | null;
  bucket_exists: boolean;
  initialized: boolean;
  snapshot_count: number;
  newest_snapshot_at: string | null;
};

export type DisasterRecoveryLastRequest = {
  requested_at: string;
  requested_by: string;
  status: "pending" | "exchanged" | "expired";
  expires_at: string | null;
  exchanged_at: string | null;
  displaced_live_token: boolean;
};

export type DisasterRecoveryReadiness = {
  automation_server_id: string;
  name: string;
  domain: string | null;
  repo: DisasterRecoveryRepo;
  blockers: string[];
  warnings: string[];
  recoverable: boolean;
  // Redeeming the OTP replaces the server's token, so recovering a server that
  // is still alive cuts it off from the AOC until it re-registers. Only the
  // operator's confirmation redeems it — the command verifies their encryption
  // key and reads the restore plan first, both on the OTP alone.
  displaces_live_token: boolean;
  // The CLI version the server last reported, and which one the recovery will
  // actually install. The version is inside every backup too, but restic
  // encrypts it and the key is never escrowed, so this self-report is the only
  // copy the AOC can read. pinned_version is null whenever the exact build
  // cannot be reproduced (never reported, built from source, or a release
  // predating `recover server`) — version_note always says which.
  reported_version: string | null;
  reported_version_at: string | null;
  pinned_version: string | null;
  version_note: string;
  last_request: DisasterRecoveryLastRequest | null;
};

export type DisasterRecoveryOTP = DisasterRecoveryReadiness & {
  otp: string;
  expires_at: string | null;
  aoc_api_url: string;
  command: string;
  // The command prompts for the encryption key; there is no path to hand out,
  // because it is never stored here.
  key_prompted: boolean;
};

export async function getDisasterRecoveryReadiness(automationServerId: string) {
  const activeOrg = await getActiveOrgFromCookies();
  const apiClient = await authenticatedBitswanBackendInstance();
  const response = await apiClient.get(
    `/automation-servers/disaster-recovery-otp?automation_server_id=${automationServerId}`,
    {
      headers: {
        "X-Org-Id": activeOrg?.id ?? "",
      },
    },
  );

  return response.data as DisasterRecoveryReadiness;
}

export async function createDisasterRecoveryOTP(automationServerId: string) {
  const activeOrg = await getActiveOrgFromCookies();
  const apiClient = await authenticatedBitswanBackendInstance();
  const response = await apiClient.post(
    "/automation-servers/disaster-recovery-otp",
    { automation_server_id: automationServerId },
    {
      headers: {
        "X-Org-Id": activeOrg?.id ?? "",
      },
    },
  );

  return response.data as DisasterRecoveryOTP;
}

export async function getHetznerOptions(orgId?: string) {
  const activeOrg = orgId ? null : await getActiveOrgFromCookies();
  const apiClient = await authenticatedBitswanBackendInstance();
  const response = await apiClient.get(
    '/automation-servers/hetzner-options',
    {
      headers: {
        "X-Org-Id": orgId ?? activeOrg?.id ?? "",
      },
    }
  );
  return response.data as HetznerOptions;
}

export async function provisionHetznerServer(data: {
  name: string;
  subdomain: string;
  server_config_id: string;
  region: string;
}, orgId?: string) {
  const activeOrg = orgId ? null : await getActiveOrgFromCookies();
  const apiClient = await authenticatedBitswanBackendInstance();
  const response = await apiClient.post(
    '/automation-servers/provision-hetzner',
    data,
    {
      headers: {
        "X-Org-Id": orgId ?? activeOrg?.id ?? "",
      },
    }
  );
  return response.data as {
    automation_server_id: string;
    provisioning_status: string;
    domain: string;
  };
}


export async function checkSubdomainAvailability(subdomain: string, orgId?: string) {
  const activeOrg = orgId ? null : await getActiveOrgFromCookies();
  const apiClient = await authenticatedBitswanBackendInstance();
  const response = await apiClient.get(
    `/automation-servers/check-subdomain?subdomain=${encodeURIComponent(subdomain)}`,
    {
      headers: {
        "X-Org-Id": orgId ?? activeOrg?.id ?? "",
      },
    }
  );
  return response.data as { available: boolean; error?: string };
}

export async function checkProvisioningStatus(automationServerId: string, orgId?: string) {
  const activeOrg = orgId ? null : await getActiveOrgFromCookies();
  const apiClient = await authenticatedBitswanBackendInstance();
  const response = await apiClient.get(
    `/automation-servers/provisioning-status?automation_server_id=${automationServerId}`,
    {
      headers: {
        "X-Org-Id": orgId ?? activeOrg?.id ?? "",
      },
    }
  );
  return response.data as ProvisioningStatus;
}
